// The `list.outreach.routing` namespace: campaign routing rules for auto-assign (PREC-4219). Kept in
// its own file so the over-length translation files stay within the architecture-gate ratchet.
export const enCampaignRouting = {
	summary: {
		title: "Auto-assign routing",
		intro:
			"Leads that are auto-assigned or assigned without picking a campaign go to the first campaign whose rule matches. Rules are evaluated top to bottom: the first match wins.",
		editRules: "Edit rules",
		addRule: "Add rule",
		ruleLine: "If {{conditions}} → {{campaign}}",
		skipped: "Campaign {{state}}: skipped",
		emptyDefault:
			"No rules yet: every auto-assigned lead goes to the default campaign {{campaign}}. Add a rule to send leads to a campaign by country, persona, company segment and more.",
		emptyNoDefault:
			"No rules yet, and this list has no default campaign that can take leads: auto-assigned leads stay in New. Add a rule to send leads to a campaign by country, persona, company segment and more.",
		emptyNoCampaign:
			"No rules yet: auto-assigned leads are not given a campaign and stay in New. Add a rule to send leads to a campaign by country, persona, company segment and more.",
		loadFailed: "The routing rules could not be loaded. Reload the page to try again.",
		loading: "Loading routing rules…",
	},
	fallback: {
		label: "No rule matched:",
		summaryDefault: "No rule matched → default campaign {{campaign}}",
		summaryDefaultUnusable:
			"No rule matched → default campaign {{campaign}}, which cannot take leads ({{state}}): the lead stays in New",
		summaryNoDefault: "No rule matched → no default campaign: the lead stays in New",
		summaryNone: "No rule matched → don't assign: the lead stays in New",
		optionDefault: "Assign to the default campaign ({{campaign}})",
		optionDefaultMissing: "Assign to the default campaign (none set)",
		optionNone: "Don't assign — leave the lead in New",
	},
	editor: {
		title: "Campaign routing rules",
		subtitle:
			"Rules are evaluated top to bottom: the first match wins. They decide the campaign for leads that are auto-assigned or assigned without picking a campaign.",
		enrollmentOff:
			"Auto-assign is off for this list: saving routes each lead to a campaign (preassigned, shown on the lead) but enrolls nobody and sends nothing. You confirm leads into their campaigns yourself.",
		enrollmentOn:
			"Auto-assign is on for this list: leads that pass the minimum score and the usual checks are enrolled into their routed campaign, and sending starts once that campaign runs.",
		save: "Save rules",
		addRule: "+ Add rule",
		addCondition: "+ condition",
		and: "AND",
		thenAssignTo: "THEN ASSIGN TO",
		selectCampaign: "Select campaign…",
		moveUp: "Move rule up",
		moveDown: "Move rule down",
		removeRule: "Remove rule",
		removeCondition: "Remove condition",
		removeValue: "Remove value",
		selectValues: "Select values…",
		searchValues: "Type to search…",
		valuesFailed: "The values could not be loaded. Close the editor and open it again to retry.",
		searchFailed: "The values could not be loaded.",
		retry: "Retry",
		typeText: "Type a word and press Enter",
		from: "From",
		to: "To",
		scoringHint: "A lead without a score from your lead scoring does not match this condition.",
		waitNote: "Leads wait until the campaign is activated.",
		pausedNote: "Leads wait until the campaign runs again.",
		endedNote: "Leads are assigned, but the campaign has ended and sends nothing more.",
		skippedNote:
			"This campaign can't accept leads. Until it is fixed, leads that match go on to the next rule.",
		deletedNote: "This campaign was deleted. Leads that match go on to the next rule.",
		saveFailed: "The routing rules could not be saved.",
		savePending:
			"Your last change to these rules is still being saved. They open here as soon as the save has finished.",
		incomplete: "Rule {{number}}: {{problem}}",
		problems: {
			noCampaign: "pick a campaign to assign to.",
			noValues: "add at least one value to each condition.",
			noRange: "enter a from or a to number of employees.",
			badRange: "the from number is above the to number.",
			tooManyValues: "a condition takes at most {{max}} values.",
			tooManyConditions: "a rule takes at most {{max}} conditions.",
		},
		tooManyRules: "A list takes at most {{max}} rules.",
		limitConditions: "At most {{max}} conditions per rule.",
		limitRules: "At most {{max}} rules per list.",
	},
	fields: {
		groupLead: "Lead",
		groupCompany: "Company",
		groupScoring: "Lead scoring",
		PERSONAL_COUNTRY: "Personal country",
		COMPANY_COUNTRY: "Company country",
		PERSONA_SEGMENT: "Persona segment",
		COMPANY_SEGMENT: "Company segment",
		PRIORITY: "Priority",
		SENIORITY: "Seniority",
		INDUSTRY: "Industry",
		JOB_TITLE: "Job title",
		NUMBER_OF_EMPLOYEES: "Number of employees",
		PROFILE_LANGUAGE: "Profile language",
	},
	operators: {
		IS_ANY_OF: "is any of",
		IS_NONE_OF: "is none of",
		IS_EMPTY: "is empty",
		IS_NOT_EMPTY: "is not empty",
		CONTAINS: "contains",
		DOES_NOT_CONTAIN: "does not contain",
		BETWEEN: "is between",
	},
	condition: {
		values: "{{field}} {{operator}} {{values}}",
		empty: "{{field}} {{operator}}",
		between: "{{field}} is between {{from}} and {{to}}",
		atLeast: "{{field}} is at least {{from}}",
		atMost: "{{field}} is at most {{to}}",
		valueSeparator: ", ",
		and: " AND ",
	},
	targetStates: {
		VALID: "Active",
		PAUSED: "Paused",
		NOT_ACTIVATED: "Ready",
		ENDED: "Ended",
		INVALID: "Invalid",
		DELETED: "Deleted",
	},
	reason: {
		rule: "Routed by rule {{number}}: {{conditions}}",
		ruleUnknown: "Routed by a routing rule",
		ruleGone: "Routed by a rule that no longer exists",
		default: "Default campaign (no rule matched)",
	},
	settingsLine: {
		withRules_one:
			"Leads go to the first matching routing rule ({{count}} rule), otherwise to {{fallback}}.",
		withRules_other:
			"Leads go to the first matching routing rule ({{count}} rules), otherwise to {{fallback}}.",
		noRules: "No routing rules: leads go to {{fallback}}.",
		fallbackDefault: "the default campaign {{campaign}}",
		fallbackNoDefault: "no campaign (this list has no default campaign that can take leads)",
		fallbackNone: "no campaign (they stay in New)",
		loadFailed: "Where auto-assigned leads go could not be loaded.",
		editLink: "Edit routing rules",
	},
	confirm: {
		action: "Confirm preassigned campaigns",
		actionDescription: "Enroll each selected lead into the campaign it already holds.",
		title: "Confirm preassigned campaigns",
		body_one:
			"{{count}} lead will be confirmed. It goes into its own preassigned campaign, through the usual sender, campaign and eligibility checks.",
		body_other:
			"{{count}} leads will be confirmed. Each one goes into its own preassigned campaign, through the usual sender, campaign and eligibility checks.",
		skippedBefore_one: "{{count}} selected lead is skipped: {{reasons}}.",
		skippedBefore_other: "{{count}} selected leads are skipped: {{reasons}}.",
		bodyServerResolved:
			"Every selected lead that holds a preassigned campaign and is not in a campaign yet is confirmed, each into its own campaign, through the usual sender, campaign and eligibility checks. Leads already in a campaign, without a preassigned campaign or outside the list's leads are skipped. How many were confirmed and skipped is shown once the server has answered.",
		serverChecks:
			"A lead the server finds cannot go yet (for example its campaign is being deleted) is skipped too, and the result names it.",
		noSingleCampaign:
			"No campaign is chosen here: leads without a preassigned campaign are skipped, not moved.",
		submit: "Confirm",
		failed: "The leads could not be confirmed.",
		result_one: "Confirmed {{count}} lead.",
		result_other: "Confirmed {{count}} leads.",
		skippedIntro: "Skipped:",
		skipReasons: {
			NO_CAMPAIGN: "{{count}} without a preassigned campaign",
			NOT_INCLUDED: "{{count}} not among the list's leads",
			ALREADY_ENROLLED: "{{count}} already in a campaign",
			CAMPAIGN_NOT_USABLE: "{{count}} whose campaign can't take leads",
			PLACEHOLDER: "{{count}} still being looked up",
			FAILED: "{{count}} failed",
		},
	},
};
